// The ACC line on a project card: the linked file's path, and while the server
// processes it (heavy lane: download -> read -> publish) a spinner, the stage, the
// progress and when it will be ready. Nothing for a project that is not linked.
import React from "react";
import { Spin, Tooltip } from "antd";
import { LoadingOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useAccLink, isAccBusy } from "./useAccLinks";
import AccCloudIcon from "./AccCloudIcon";
import "./acc.scss";

const STAGES = {
  download: "Downloading from ACC",
  links: "Fetching linked models",
  bank: "Reading the Revit model",
  publish: "Publishing the model",
};

export function formatEta(s) {
  if (s == null || !Number.isFinite(Number(s))) return "";
  const n = Math.max(0, Math.round(Number(s)));
  if (n < 60) return `~${Math.max(5, Math.round(n / 5) * 5)} s`;
  if (n < 3600) return `~${Math.round(n / 60)} min`;
  const h = Math.floor(n / 3600);
  const m = Math.round((n % 3600) / 60);
  return `~${h} h${m ? ` ${m} min` : ""}`;
}

const when = (d) => (d ? new Date(d).toLocaleString() : "");

export default function AccCardStatus({ projectId }) {
  const link = useAccLink(projectId);
  if (!link) return null;
  const run = link.run;
  const busy = isAccBusy(link);
  const pct = run ? Math.round((run.progress || 0) * 100) : 0;

  return (
    <div className="acc-card-status">
      <Tooltip title={link.path || link.fileName}>
        <div className="acc-card-path">
          <AccCloudIcon active size={14} />
          <span className="acc-card-path-text">{link.path || link.fileName}</span>
        </div>
      </Tooltip>

      {busy && (
        <div className="acc-card-progress">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />
          <div className="acc-card-progress-text">
            <div>
              {run.status === "queued"
                ? `Queued${run.queuePosition > 1 ? ` (#${run.queuePosition})` : ""}`
                : `${STAGES[run.stage] || "Processing"} · ${pct}%`}
              {run.versionNumber ? ` · ACC v${run.versionNumber}` : ""}
            </div>
            <div className="acc-card-eta">
              {run.etaSeconds != null ? `Ready in ${formatEta(run.etaSeconds)}` : "Estimating…"}
            </div>
            {run.status === "running" && (
              <div className="acc-card-bar">
                <div style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        </div>
      )}

      {!busy && run && run.status === "failed" && (
        <Tooltip title={run.error}>
          <div className="acc-card-failed">
            <ExclamationCircleOutlined /> ACC v{run.versionNumber || "?"} failed to process
          </div>
        </Tooltip>
      )}

      {!busy && run && run.status === "done" && (
        <Tooltip title={(run.warnings || []).join("\n") || undefined}>
          <div className="acc-card-done">
            Synced ACC v{link.lastVersionNumber || run.versionNumber} · {when(run.finishedAt)}
            {run.warnings && run.warnings.length ? ` · ${run.warnings.length} note(s)` : ""}
          </div>
        </Tooltip>
      )}
    </div>
  );
}
